import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ContentPillarsContext = createContext(null);

export function ContentPillarsProvider({ children }) {
  const { user } = useAuth();
  const [setsByProject, setSetsByProject] = useState({});
  const [limitsByProject, setLimitsByProject] = useState({});
  const [loading, setLoading] = useState(false);

  const refreshSets = useCallback(async (projectId) => {
    if (!projectId || !user) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/content-pillar-sets`);
      setSetsByProject((prev) => ({ ...prev, [projectId]: data.pillar_sets || [] }));
      setLimitsByProject((prev) => ({ ...prev, [projectId]: data.limits || {} }));
    } catch {
      // silently ignore
    } finally {
      setLoading(false);
    }
  }, [user]);

  const createSet = useCallback(async (projectId, body) => {
    const { data } = await api.post(`/projects/${projectId}/content-pillar-sets`, body);
    await refreshSets(projectId);
    return data;
  }, [refreshSets]);

  const deleteSet = useCallback(async (projectId, setId) => {
    await api.delete(`/content-pillar-sets/${setId}`);
    setSetsByProject((prev) => ({
      ...prev,
      [projectId]: (prev[projectId] || []).filter((s) => s.id !== setId),
    }));
  }, []);

  const value = useMemo(() => ({
    setsByProject, limitsByProject, loading, refreshSets, createSet, deleteSet,
  }), [setsByProject, limitsByProject, loading, refreshSets, createSet, deleteSet]);

  return <ContentPillarsContext.Provider value={value}>{children}</ContentPillarsContext.Provider>;
}

export function useContentPillars() {
  const ctx = useContext(ContentPillarsContext);
  if (!ctx) throw new Error("useContentPillars must be inside ContentPillarsProvider");
  return ctx;
}
